import type { RfpDetail, GatesSource } from "../api/CrmApi";
import { GatesVerdictPill, formatDateTime, formatDate } from "./crmUi";
import StatusPill from "../../../shared/StatusPill";

// Human-readable labels for the /rfp-gates `kind` / condition `type` slugs; an unknown slug falls back
// to the slug with underscores turned into spaces, so a new type never renders blank.
const KIND_LABELS: Record<string, string> = {
  missing_document: "Missing document",
  unsupported_claim: "Unsupported CollabP claim",
  unclear_scope: "Unclear scope",
  unclear_personnel: "Unclear personnel listing",
  unclear_wording: "Unclear wording",
};

function humanize(slug: string | undefined, labels: Record<string, string> = {}): string {
  if (!slug) return "";
  return labels[slug] ?? slug.replace(/_/g, " ");
}

function sourceText(source?: GatesSource): string | null {
  if (!source) return null;
  const parts = [source.file, source.location].filter(Boolean);
  return parts.length ? parts.join(" · ") : null;
}

function Quote({ text, source }: { text?: string; source?: GatesSource }) {
  const src = sourceText(source);
  if (!text && !src) return null;
  return (
    <div className="mt-1 text-[11px] text-muted-foreground">
      {text && <p className="italic break-words">“{text}”</p>}
      {src && <p className="break-words">{src}</p>}
    </div>
  );
}

function Group({ title, count, children }: { title: string; count: number; children: React.ReactNode }) {
  if (count === 0) return null;
  return (
    <div>
      <p className="text-xs font-medium text-muted-foreground mb-1.5">
        {title} <span className="font-normal">({count})</span>
      </p>
      <ul className="space-y-2">{children}</ul>
    </div>
  );
}

function Item({ children }: { children: React.ReactNode }) {
  return <li className="text-sm text-foreground rounded-lg border border-border bg-surface px-3 py-2">{children}</li>;
}

/** The /rfp-gates result for one RFP: verdict, then stop reasons, conditions, items to verify, missing
 *  documents and CollabP discrepancies. Renders nothing for an RFP that was never gate-checked. */
export default function GatesSection({ rfp }: { rfp: RfpDetail | null }) {
  const verdict = rfp?.gates_verdict;
  if (!rfp || !verdict) {
    return (
      <div>
        <p className="text-xs font-medium text-muted-foreground mb-2">Qualification gates</p>
        <p className="text-sm text-muted-foreground">Not checked yet — run <span className="font-mono">/rfp-gates</span> for this RFP.</p>
      </div>
    );
  }
  const g = rfp.gates ?? {};
  const stops = g.stop_reasons ?? [];
  const conditions = g.conditions ?? [];
  const toVerify = g.to_verify ?? [];
  const notSupplied = g.not_supplied ?? [];
  const discrepancies = g.collabp_discrepancies ?? [];
  const nothing = !stops.length && !conditions.length && !toVerify.length && !notSupplied.length && !discrepancies.length;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <p className="text-xs font-medium text-muted-foreground">Qualification gates</p>
        <GatesVerdictPill verdict={verdict} />
        {g.deadline_passed && <StatusPill tone="neutral" size="sm" title="Every bid-action date has passed — /rfp-check owns the go/no-go">closed</StatusPill>}
        {rfp.gates_at && <span className="text-[11px] text-muted-foreground">checked {formatDateTime(rfp.gates_at)}</span>}
      </div>

      {nothing && <p className="text-sm text-muted-foreground">No conditions, holds or stop reasons were recorded.</p>}

      <Group title="Stop reasons" count={stops.length}>
        {stops.map((s, i) => (
          <Item key={i}>
            <p className="font-medium">{s.reason ?? s.rule ?? humanize(s.item)}</p>
            <Quote text={s.quote} source={s.source} />
          </Item>
        ))}
      </Group>

      <Group title="Conditions the prime must satisfy" count={conditions.length}>
        {conditions.map((c, i) => (
          <Item key={i}>
            <p>
              <span className="font-medium capitalize">{humanize(c.type)}</span>
              {c.scope && <span className="text-muted-foreground"> — {c.scope}</span>}
            </p>
            {c.requirement && <p className="text-xs mt-0.5 break-words">{c.requirement}</p>}
            {c.per_prime_check && <p className="text-[11px] text-muted-foreground mt-0.5">Check per prime: {c.per_prime_check}</p>}
            {c.assumption && <p className="text-[11px] text-muted-foreground mt-0.5">Assumption: {c.assumption}</p>}
            {c.raise_by && <p className="text-[11px] text-muted-foreground mt-0.5">Raise by: {c.raise_by}</p>}
            <Quote text={c.quote} source={c.source} />
          </Item>
        ))}
      </Group>

      <Group title="To verify" count={toVerify.length}>
        {toVerify.map((t, i) => (
          <Item key={i}>
            <div className="flex items-start gap-2 flex-wrap">
              <p className="font-medium break-words flex-1 min-w-0">{t.what}</p>
              {t.impact === "commercial_only" ? (
                <StatusPill tone="neutral" size="sm" title="Only money terms (insurance limits, rates, bond amounts…) — safe to release quickly">money terms only</StatusPill>
              ) : (
                <StatusPill tone="orange" size="sm" title="Could change whether we can pitch this RFP">could change eligibility</StatusPill>
              )}
              {t.time_critical && <StatusPill tone="danger" size="sm">urgent</StatusPill>}
            </div>
            {t.why && <p className="text-xs text-muted-foreground mt-0.5 break-words">{t.why}</p>}
            <p className="text-[11px] text-muted-foreground mt-0.5">
              {[humanize(t.kind, KIND_LABELS), t.reference, t.ask ? `ask: ${t.ask}` : null, t.nearest_deadline ? `next date: ${formatDate(t.nearest_deadline)}` : null]
                .filter(Boolean)
                .join(" · ")}
            </p>
          </Item>
        ))}
      </Group>

      <Group title="Documents not supplied" count={notSupplied.length}>
        {notSupplied.map((n, i) => (
          <Item key={i}>
            <p className="font-medium break-words">{n.document}</p>
            {n.what_to_obtain && <p className="text-xs mt-0.5 break-words">{n.what_to_obtain}</p>}
            <p className="text-[11px] text-muted-foreground mt-0.5">
              {[n.referenced_in, n.materiality ? `material: ${n.materiality}` : null].filter(Boolean).join(" · ")}
            </p>
          </Item>
        ))}
      </Group>

      <Group title="Discrepancies for CollabP" count={discrepancies.length}>
        {discrepancies.map((d, i) => (
          <Item key={i}>
            <p className="break-words">{d.claim}</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              {[d.doc, humanize(d.kind), d.original_says ? `original: ${d.original_says}` : null].filter(Boolean).join(" · ")}
            </p>
          </Item>
        ))}
      </Group>
    </div>
  );
}
